import * as React from 'react';

import { CSSObject, Theme } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import MuiDrawer from '@mui/material/Drawer';
import MenuList from '@mui/material/MenuList';
import NotificationsView from '../NotificationsView';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { defaultThemeColor } from '../../config/themeConfig';
import { styled } from '@mui/material/styles';
import { useCapabilityAccess } from '../../hooks/useCapabilityAccess';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { isKeycloakEnabled } from '../../config/authConfig';
import { useAuthSafe, useKeycloak } from '../ConditionalAuthProvider';
import { logoutUser } from '../../redux/slices/userSlice';
import { clearActionTrackingState } from '../../redux/slices/actionsSlice';
import { currentTheme } from '../../config/themeConfig';
import BreadcrumbBar from '../common/BreadcrumbBar/BreadcrumbBar';
import LayoutFooter from '../common/WelllynxFooter';
import { RtuDetailChromeProvider } from '../../context/RtuDetailChromeContext';
import { isSameAppLocation } from '../../utils/navigationLocation';

// Import icons
import adminIcon from '../../assets/img/icons/B_Admin.png';
import dashboardIcon from '../../assets/img/icons/B_Dashboard.svg';
import { RTU_ICONS } from '../../constants/rtuIcons';
import { closeAllRtuWebSessions } from '../../services/rtuWebGatewaySessions';

const assetsSidebarIcon = <img src={RTU_ICONS.ASSETS} alt="Assets" style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />;
const assetsMenuIcon = <img src={RTU_ICONS.ASSETS} alt="" style={{ width: 18, height: 18 }} />;

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(0, 1),
  minHeight: 45, // match compact AppBar height to avoid extra gap
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: '#fff',
  color: theme.palette.text.primary,
  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme),
      backgroundColor: defaultThemeColor,
      color: '#fff',
    },
  }),
);

interface NavigationSubItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

export default function WelllynxLayout({ children }: { children: React.ReactNode }) {
  const layoutTheme = currentTheme;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [navMenuAnchorEl, setNavMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [navMenuTitle, setNavMenuTitle] = React.useState('');
  const [navMenuItems, setNavMenuItems] = React.useState<NavigationSubItem[]>([]);
  const navMenuCloseTimerRef = React.useRef<number | null>(null);
  const navMenuPaperRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const { logout: backendLogout } = useAuthSafe();
  const capabilityAccess = useCapabilityAccess();

  const handleLogout = async () => {
    dispatch(clearActionTrackingState());
    dispatch(logoutUser());

    // End the RTU web sessions opened from this tab while the outgoing token is still valid (best effort, never blocks).
    closeAllRtuWebSessions('DMS_SIGNED_OUT', keycloak?.token);

    if (isKeycloakEnabled()) {
      if (keycloak) {
        try {
          await keycloak.logout({ redirectUri: window.location.origin });
        } catch (e) {
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
    } else {
      try {
        await backendLogout();
      } finally {
        navigate('/login');
      }
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const clearNavMenuCloseTimer = React.useCallback(() => {
    if (navMenuCloseTimerRef.current !== null) {
      window.clearTimeout(navMenuCloseTimerRef.current);
      navMenuCloseTimerRef.current = null;
    }
  }, []);

  const closeNavMenu = React.useCallback(() => {
    clearNavMenuCloseTimer();
    setNavMenuAnchorEl(null);
    setNavMenuItems([]);
    setNavMenuTitle('');
  }, [clearNavMenuCloseTimer]);

  const scheduleCloseNavMenu = React.useCallback(() => {
    clearNavMenuCloseTimer();
    navMenuCloseTimerRef.current = window.setTimeout(() => {
      closeNavMenu();
    }, 180);
  }, [clearNavMenuCloseTimer, closeNavMenu]);

  const scheduleCloseNavMenuIfFocusOutside = React.useCallback((target: EventTarget | null) => {
    const nextTarget = target instanceof Node ? target : null;
    if (
      nextTarget
      && (navMenuAnchorEl?.contains(nextTarget) || navMenuPaperRef.current?.contains(nextTarget))
    ) {
      return;
    }
    scheduleCloseNavMenu();
  }, [navMenuAnchorEl, scheduleCloseNavMenu]);

  const openNavMenu = React.useCallback((
    anchor: HTMLElement,
    title: string,
    subItems: NavigationSubItem[]
  ) => {
    clearNavMenuCloseTimer();
    setNavMenuAnchorEl(anchor);
    setNavMenuTitle(title);
    setNavMenuItems(subItems);
  }, [clearNavMenuCloseTimer]);

  React.useEffect(() => clearNavMenuCloseTimer, [clearNavMenuCloseTimer]);

  const menuItems = [
    {
      text: t('navigation.dashboard'),
      icon: <img src={dashboardIcon} alt={t('navigation.dashboard')} style={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />,
      path: '/dashboard',
    },
    {
      text: t('navigation.assets'),
      icon: assetsSidebarIcon,
      path: '',
      testId: 'sidebar-assets-button',
      subItems: [
        { text: t('navigation.groupedAssets'), icon: assetsMenuIcon, path: '/rtutreelist' },
        { text: t('navigation.assetList'), icon: assetsMenuIcon, path: '/rtulist' },
        ...(FEATURE_FLAGS.mapView && (!FEATURE_FLAGS.mapViewRequiresBeta || capabilityAccess.canAccessBetaFeatures)
          ? [{ text: t('navigation.mapView'), icon: <MapIcon />, path: '/mapview' }]
          : []),
      ],
    },
    // Data Reception is a normal Operator surface; internal observability access
    // (support staff without a DMS group role) can still reach it too.
    ...(FEATURE_FLAGS.ingestionObservability
      && (capabilityAccess.canUseDmsOperatorFeatures || capabilityAccess.canAccessInternalObservability)
      ? [{ text: t('observability.navTitle'), icon: <QueryStatsIcon />, path: '/observability/ingestion' }]
      : []),
    // Action history/artifacts are a read surface available to Operator and
    // Administrator alike; only launching/retrying/cancelling is Administrator-only.
    ...(FEATURE_FLAGS.actionsTracking && capabilityAccess.canUseDmsOperatorFeatures
      ? [
          { text: t('actionsWorkbench.title', { defaultValue: 'All Actions' }), icon: <HistoryIcon />, path: '/actions' },
          { text: t('audit.report.title', { defaultValue: 'Reports' }), icon: <FactCheckIcon />, path: '/audit/report' },
        ]
      : []),
  ];

  const isPathActive = React.useCallback((path: string) => {
    if (!path) {
      return false;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  }, [location.pathname]);

  const navigateIfChanged = React.useCallback(
    (destination: string) => {
      if (!isSameAppLocation(destination, location)) {
        navigate(destination);
      }
    },
    [location, navigate]
  );

  const isAssetsDetailRoute = location.pathname.startsWith('/rtupage/');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar variant="dense" sx={{ minHeight: 45, display: 'flex', alignItems: 'center' }}>
          <img src={layoutTheme?.icon || layoutTheme?.logo} alt="Logo" style={{ height: 28, marginRight: 10, marginLeft: -8, display: 'inline-flex', verticalAlign: 'middle' }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontSize: '1rem' }}>
            {t('Intelevate® Edge Devices Management System')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 0.5 }}>
            {/* Keep notification entry point disabled until notification flows are implemented end-to-end. */}
            {FEATURE_FLAGS.showNotifications ? <NotificationsView /> : null}
            <Tooltip title={t('navigation.profile')} arrow>
              <IconButton
                size="small"
                aria-label={t('navigation.profile')}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{ p: 0.5 }}
              >
                <img src={adminIcon} alt="" style={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 120,
                    '& .MuiMenuItem-root': {
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                    },
                  },
                },
              }}
            >
              {/* Keep profile menu option hidden until profile management screen is ready. */}
              {FEATURE_FLAGS.showProfileMenuItem ? (
                <MenuItem onClick={handleClose}>
                  <PersonIcon sx={{ fontSize: 16, mr: 1 }} />
                  {t('navigation.profile')}
                </MenuItem>
              ) : null}
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleLogout();
                }}
              >
                <LogoutIcon sx={{ fontSize: 16, mr: 1 }} />
                {t('logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={false}>
        <DrawerHeader>
          <img src={layoutTheme?.icon || layoutTheme?.logo} alt="brand" style={{ height: 24, marginLeft: 8 }} />
        </DrawerHeader>
        <Divider />
        <List sx={{ flexGrow: 1 }}>
          {menuItems.map((item) => {
            const navButton = (
              <ListItemButton
                data-testid={item.testId}
                aria-label={item.text}
                aria-haspopup={item.subItems ? 'menu' : undefined}
                aria-expanded={item.subItems ? Boolean(navMenuAnchorEl) : undefined}
                selected={item.subItems
                  ? item.subItems.some((subItem) => isPathActive(subItem.path)) || isAssetsDetailRoute
                  : isPathActive(item.path)}
                sx={{
                  minHeight: 40,
                  justifyContent: 'center',
                  px: 1.25,
                  '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: 'rgba(255,255,255,0.18)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                  },
                }}
                onMouseEnter={(event) => {
                  if (item.subItems) {
                    openNavMenu(event.currentTarget, item.text, item.subItems);
                  }
                }}
                onMouseLeave={() => {
                  if (item.subItems) {
                    scheduleCloseNavMenu();
                  }
                }}
                onFocus={(event) => {
                  if (item.subItems) {
                    openNavMenu(event.currentTarget, item.text, item.subItems);
                  }
                }}
                onBlur={(event) => {
                  if (item.subItems) {
                    scheduleCloseNavMenuIfFocusOutside(event.relatedTarget);
                  }
                }}
                onClick={(event) => {
                  if (item.subItems) {
                    openNavMenu(event.currentTarget, item.text, item.subItems);
                    return;
                  }
                  closeNavMenu();
                  navigateIfChanged(item.path);
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 'auto',
                    justifyContent: 'center',
                    color: '#fff',
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                  sx={{ opacity: 0, pr: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                />
              </ListItemButton>
            );

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  {item.subItems ? navButton : (
                    <Tooltip title={item.text} placement="right" arrow>
                      {navButton}
                    </Tooltip>
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
        <Popper
          anchorEl={navMenuAnchorEl}
          open={Boolean(navMenuAnchorEl)}
          placement="right-start"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 0],
              },
            },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        >
          <Paper
            ref={navMenuPaperRef}
            elevation={6}
            onMouseEnter={clearNavMenuCloseTimer}
            onMouseLeave={scheduleCloseNavMenu}
            onFocus={clearNavMenuCloseTimer}
            onBlur={(event) => scheduleCloseNavMenuIfFocusOutside(event.relatedTarget)}
            sx={{
              minWidth: 190,
              overflow: 'hidden',
            }}
          >
            <MenuList
              dense
              autoFocusItem={false}
              aria-label={navMenuTitle || undefined}
              sx={{
                '& .MuiMenuItem-root': {
                  fontSize: '0.82rem',
                  minHeight: 36,
                  gap: 1,
                },
              }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 700 }}>
                {navMenuTitle}
              </MenuItem>
              {navMenuItems.map((subItem) => (
                <MenuItem
                  key={subItem.path}
                  selected={isPathActive(subItem.path)}
                  onClick={() => {
                    closeNavMenu();
                    navigateIfChanged(subItem.path);
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28, color: 'inherit', '& .MuiSvgIcon-root': { fontSize: 18 } }}>
                    {subItem.icon}
                  </ListItemIcon>
                  {subItem.text}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
        <Divider />
        {capabilityAccess.canManageDmsSettings && (
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <Tooltip title={t('navigation.masterSettings', { defaultValue: 'Master Settings' })} placement="right" arrow>
                <ListItemButton
                  sx={{
                    minHeight: 40,
                    justifyContent: 'center',
                    px: 1.25,
                  }}
                  onClick={() => {
                    closeNavMenu();
                    navigateIfChanged('/admin/settings/reachability/ping-templates');
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 'auto',
                      justifyContent: 'center',
                      color: '#fff',
                      '& .MuiSvgIcon-root': { fontSize: 22 },
                    }}
                  >
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('navigation.masterSettings', { defaultValue: 'Master Settings' })}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    sx={{ opacity: 0, pr: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        )}
      </Drawer>
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh',
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'hidden',
          p: 0, 
          pt: 1 
        }}
      >
        <DrawerHeader />
        <RtuDetailChromeProvider>
          <BreadcrumbBar />
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto' }}>
            {children}
          </Box>
        </RtuDetailChromeProvider>
        <LayoutFooter drawerOpen={false} themeConfig={layoutTheme} />
      </Box>
    </Box>
  );
}
